import { useEffect, useMemo, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IMediaFile } from "@dc-ireland/shared"
import { isImageUrl, resolveImageUrl } from "../../lib/imageUtils"
import AdminAlerts from "../../components/admin/AdminAlerts"

const AdminUpload = () => {
  const [files, setFiles] = useState<IMediaFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [name, setName] = useState("")
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [query, setQuery] = useState("")

  const loadFiles = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiClient.getMediaFiles()
      setFiles(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load media library")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadFiles()
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (selectedFiles.length === 0) return

    setUploading(true)
    setUploadProgress(null)
    setError(null)
    setSuccess(null)
    try {
      const uploaded = await apiClient.uploadFiles(selectedFiles, {
        name: selectedFiles.length === 1 ? name.trim() || undefined : undefined,
        onProgress: (completed, total) => setUploadProgress(`${completed} of ${total}`),
      })
      setSelectedFiles([])
      setName("")
      setSuccess(
        uploaded.length === 1
          ? "File uploaded to Cloudinary and added to the library."
          : `${uploaded.length} files uploaded to Cloudinary and added to the library.`
      )
      ;(e.target as HTMLFormElement).reset()
      await loadFiles()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed")
      await loadFiles()
    } finally {
      setUploading(false)
      setUploadProgress(null)
    }
  }

  const handleRename = async (id: string) => {
    if (!renameValue.trim()) return
    setError(null)
    try {
      await apiClient.renameMediaFile(id, renameValue.trim())
      setRenamingId(null)
      setRenameValue("")
      await loadFiles()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename file")
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this file? Content using this URL will break.")) return
    setError(null)
    try {
      await apiClient.deleteMediaFile(id)
      await loadFiles()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete file")
    }
  }

  const filteredFiles = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return files
    return files.filter((item) =>
      [item.name, item.filename].some((value) => value?.toLowerCase().includes(needle))
    )
  }, [files, query])

  const handleSyncFromCloudinary = async () => {
    setSyncing(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await apiClient.syncMediaFromCloudinary()
      await loadFiles()
      setSuccess(
        result.imported > 0
          ? `Imported ${result.imported} file(s) from Cloudinary (${result.folder}).`
          : `Media library is up to date (${result.found} file(s) in Cloudinary).`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sync from Cloudinary")
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Media Library</h2>
          <p className="text-secondary">
            Upload one image or a batch. Files go to Cloudinary and appear in this library.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-primary"
          onClick={() => void handleSyncFromCloudinary()}
          disabled={syncing || uploading}
        >
          {syncing ? "Syncing…" : "Sync from Cloudinary"}
        </button>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-4">
          {selectedFiles.length <= 1 && (
            <label className="form-control">
              <span className="label-text">Display name</span>
              <input
                className="input input-bordered w-full max-w-md"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. worship-night-2026"
                disabled={uploading}
              />
            </label>
          )}
          <input
            type="file"
            className="file-input file-input-bordered file-input-primary w-full max-w-md"
            accept="image/*,.pdf,.doc,.docx"
            multiple
            onChange={(e) => setSelectedFiles(Array.from(e.target.files ?? []))}
            required
            disabled={uploading}
          />
          {selectedFiles.length > 1 && (
            <p className="text-sm text-secondary">
              {selectedFiles.length} files selected. Each keeps its file name.
            </p>
          )}
          <div className="card-actions justify-end">
            <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
              {uploading
                ? uploadProgress
                  ? `Uploading ${uploadProgress}…`
                  : "Uploading…"
                : selectedFiles.length > 1
                  ? `Upload ${selectedFiles.length} files`
                  : "Upload"}
            </button>
          </div>
        </div>
      </form>

      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">Uploaded files</h3>
          <input
            type="search"
            className="input input-bordered input-sm w-full max-w-xs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name"
            aria-label="Search uploaded files by name"
          />
        </div>
        {loading ? (
          <span className="loading loading-spinner loading-md" />
        ) : files.length === 0 ? (
          <p className="text-secondary">No files uploaded yet.</p>
        ) : filteredFiles.length === 0 ? (
          <p className="text-secondary">No files match “{query.trim()}”.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filteredFiles.map((item) => (
              <div key={item.id} className="card min-w-0 overflow-hidden bg-base-100 shadow-md">
                <div className="h-28 overflow-hidden bg-base-200">
                  {isImageUrl(item.url, item.mimeType) ? (
                    <img
                      src={resolveImageUrl(item.url)}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center px-4 text-center text-sm text-secondary">
                      {item.name}
                    </div>
                  )}
                </div>
                <div className="card-body min-w-0 gap-2 p-3">
                  {renamingId === item.id ? (
                    <div className="flex min-w-0 flex-col gap-2">
                      <input
                        className="input input-bordered input-sm w-full min-w-0"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                      />
                      <button type="button" className="btn btn-primary btn-sm w-full" onClick={() => void handleRename(item.id)}>
                        Save
                      </button>
                    </div>
                  ) : (
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 truncate font-medium">{item.name}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() => {
                          setRenamingId(item.id)
                          setRenameValue(item.name)
                        }}
                      >
                        Rename
                      </button>
                    </div>
                  )}
                  <a href={resolveImageUrl(item.url)} target="_blank" rel="noopener noreferrer" className="link block min-w-0 truncate text-xs">
                    {item.url}
                  </a>
                  <button type="button" className="btn btn-error btn-sm" onClick={() => void handleDelete(item.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminUpload
